import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../api/base';
import { fbPixel } from '../utils/fbPixel';
import { getLocalized } from '../utils/localized';
import UserDetailsModal from './UserDetailsModal';
import PaymentMethodModal, { Gateway, PaymentMethod } from './PaymentMethodModal';

const PRICE = 249;
const PRESETS = [1, 4, 7, 10, 25, 50, 100];

const MOBILE_VIDEOS = ['/videos/hero-1.mp4', '/videos/hero-2.mp4', '/videos/hero-3.mp4'];
const MOBILE_POSTER = '/videos/posters/hero-1.webp';
const DESKTOP_VIDEOS = ['/videos/hero-desktop.mp4'];
const DESKTOP_POSTER = '/videos/posters/hero-desktop.webp';

type Region = {
  id: number;
  code: string;
  slug: string;
  name: { ru?: string; ky?: string; en?: string };
  totalTrees: number;
  totalPlanted: number;
  isDonatable?: boolean;
};

type Mode = 'mobile' | 'desktop' | 'off';

const detectMode = (): Mode => {
  if (typeof window === 'undefined') return 'desktop';
  const motionMq = window.matchMedia('(prefers-reduced-motion: reduce)');
  const conn = (navigator as any).connection;
  const saveData = conn?.saveData === true;
  const slow = conn?.effectiveType && /^(slow-)?2g$/.test(conn.effectiveType);
  if (motionMq.matches || saveData || slow) return 'off';
  return window.matchMedia('(max-width: 768px)').matches ? 'mobile' : 'desktop';
};

const useVideoBg = () => {
  const [mode, setMode] = useState<Mode>(detectMode);
  const [videoIdx, setVideoIdx] = useState(0);
  const preloadedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const motionMq = window.matchMedia('(prefers-reduced-motion: reduce)');

    const update = () => {
      const next = detectMode();
      setMode(prev => {
        if (prev !== next) setVideoIdx(0);
        return next;
      });
    };
    mq.addEventListener('change', update);
    motionMq.addEventListener('change', update);
    return () => {
      mq.removeEventListener('change', update);
      motionMq.removeEventListener('change', update);
    };
  }, []);

  const playlist = mode === 'mobile' ? MOBILE_VIDEOS : mode === 'desktop' ? DESKTOP_VIDEOS : [];
  const poster = mode === 'mobile' ? MOBILE_POSTER : mode === 'desktop' ? DESKTOP_POSTER : '';

  const handleCanPlay = () => {
    for (const src of playlist) {
      if (preloadedRef.current.has(src)) continue;
      preloadedRef.current.add(src);
      fetch(src, { cache: 'force-cache' }).catch(() => {});
    }
  };

  const handleEnded = () => {
    if (playlist.length <= 1) return;
    setVideoIdx(i => (i + 1) % playlist.length);
  };

  return { mode, playlist, poster, videoIdx, handleCanPlay, handleEnded };
};

const HeroSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const videoBg = useVideoBg();

  const [qty, setQty] = useState<number>(7);
  const [otherQty, setOtherQty] = useState<string>('');
  const finalCount = otherQty ? Number(otherQty) || 0 : qty;
  const amount = finalCount * PRICE;

  const [regions, setRegions] = useState<Region[]>([]);
  const [unassignedTrees, setUnassignedTrees] = useState<number>(0);
  const [regionKey, setRegionKey] = useState<string>('all');
  const [regionOpen, setRegionOpen] = useState(false);
  const regionBtnRef = useRef<HTMLButtonElement | null>(null);
  const regionPopRef = useRef<HTMLDivElement | null>(null);
  const [popRect, setPopRect] = useState<{ left: number; top: number; width: number } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get<{
          regions: Region[]
          unassigned?: { totalTrees?: number }
        }>(`${API_URL}/api/regions/stats`);
        setRegions(data.regions);
        setUnassignedTrees(data.unassigned?.totalTrees || 0);
      } catch (err) {
        console.error('Error fetching regions:', err);
      }
    })();
  }, []);

  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // В hero-виджете показываем оплаченные донатерами деревья (totalTrees),
  // а не посаженные (totalPlanted) — это прогресс сбора, не отчёт о посадках.
  const treesOf = (r: Region) => r.totalTrees || 0;
  const totalRegion = unassignedTrees;
  const maxRegion = useMemo(() => Math.max(1, unassignedTrees, ...regions.map(treesOf)), [regions, unassignedTrees]);
  const allLabel = t('donate.all_kg', 'Кыргызстан');
  const regionLabel =
    regionKey === 'all'
      ? allLabel
      : (() => {
          const r = regions.find(x => x.code === regionKey);
          return r ? getLocalized(r.name, i18n.language) : allLabel;
        })();

  useEffect(() => {
    if (!regionOpen) return;
    const updateRect = () => {
      const btn = regionBtnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      setPopRect({ left: r.left, top: r.bottom + 6, width: r.width });
    };
    updateRect();

    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (regionBtnRef.current?.contains(t)) return;
      if (regionPopRef.current?.contains(t)) return;
      setRegionOpen(false);
    };
    const onScrollOrResize = () => updateRect();
    document.addEventListener('mousedown', onClick);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [regionOpen]);

  const CHECKOUT_FINIK = `${API_URL}/api/finik/checkout`;

  const handleOpenUserDetails = () => {
    if (finalCount < 1 || loading) return;
    setError(null);
    setUserDetailsOpen(true);
  };

  const handleUserDetailsSubmit = (data: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    instagramUrl: string;
  }) => {
    const { firstName, lastName, phone, email, instagramUrl } = data;
    fbPixel.initiateCheckout(amount, 'KGS', `Посадить ${finalCount} деревьев`);
    setError(null);
    setPendingPayload({
      amount: String(amount),
      currency: 'KGS',
      description: `Посадка деревьев x${finalCount} — ${firstName} ${lastName}, ${phone}, ${email}${
        instagramUrl ? `, Instagram: ${instagramUrl}` : ''
      }${regionKey !== 'all' ? `, регион: ${regionLabel}` : ''}`,
      firstName,
      lastName,
      email,
      phone,
      instagramUrl: instagramUrl || null,
      treesCount: finalCount,
      region: regionKey === 'all' ? null : regionKey
    });
    setUserDetailsOpen(false);
    setModalOpen(true);
  };

  const handleConfirmGateway = async (gateway: Gateway, method: PaymentMethod) => {
    if (!pendingPayload) return;
    try {
      setLoading(true);
      setError(null);
      fbPixel.customEvent('PaymentMethodSelected', {
        method: `${gateway}:${method}`,
        value: parseFloat(pendingPayload.amount),
        currency: 'KGS',
        trees_count: finalCount
      });
      if (gateway === 'finik') {
        const res = await axios.post(CHECKOUT_FINIK, { ...pendingPayload, method });
        const paymentUrl = res.data?.paymentUrl || res.data?.redirectUrl;
        if (paymentUrl) {
          setModalOpen(false);
          setPendingPayload(null);
          window.location.href = paymentUrl;
          return;
        }
        throw new Error('paymentUrl not returned');
      }
    } catch (err) {
      setError(t('donate.error') as string);
      fbPixel.customEvent('PaymentError', {
        error: err instanceof Error ? err.message : 'Unknown error',
        gateway,
        value: parseFloat(pendingPayload.amount)
      });
    } finally {
      setLoading(false);
    }
  };

  const sortedRegions = useMemo(
    () =>
      regions
        .filter(r => r.isDonatable !== false)
        .sort((a, b) => treesOf(b) - treesOf(a)),
    [regions]
  );

  useEffect(() => {
    if (regionKey === 'all') return;
    if (regions.length === 0) return;
    const stillAvailable = regions.some(r => r.code === regionKey && r.isDonatable !== false);
    if (!stillAvailable) setRegionKey('all');
  }, [regions, regionKey]);

  return (
    <section
      id="hero"
      style={{
        position: 'relative',
        minHeight: 'calc(100vh - 76px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#143a0e'
      }}
    >
      {videoBg.mode !== 'off' ? (
        <video
          key={`${videoBg.mode}-${videoBg.videoIdx}`}
          src={videoBg.playlist[videoBg.videoIdx]}
          poster={videoBg.poster}
          autoPlay
          muted
          playsInline
          preload="auto"
          onCanPlay={videoBg.handleCanPlay}
          onEnded={videoBg.handleEnded}
          loop={videoBg.playlist.length === 1}
          aria-hidden
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <img
          src="/uploads/fig/hero.jpg"
          alt=""
          aria-hidden
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg,rgba(10,40,15,.5),rgba(10,40,15,.2) 45%,rgba(20,58,14,.55))'
        }}
      />
      <div
        className="hero-inner"
        style={{
          position: 'relative',
          flex: 1,
          maxWidth: 1200,
          margin: '0 auto',
          padding: '24px 24px',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 44,
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap'
          }}
        >
          {/* LEFT */}
          <div style={{ flex: '1 1 300px', minWidth: 0 }}>
            <h1
              className="hero-title-42"
              style={{
                margin: 0,
                font: "700 52px/1.14 'Montserrat Alternates', sans-serif",
                color: '#fff',
                letterSpacing: '-0.01em'
              }}
            >
              {t('hero.title')}
            </h1>
            <p
              style={{
                margin: '20px 0 0',
                font: "400 20px/29px 'Golos Text', sans-serif",
                color: 'rgba(255,255,255,.92)'
              }}
            >
              {t('hero.subtitle')}
            </p>
          </div>

          {/* RIGHT — glass card */}
          <div
            className="hero-glass-card"
            style={{
              width: 455,
              maxWidth: '100%',
              borderRadius: 32,
              background: 'rgba(0,0,0,.3)',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 0 0 1px rgba(255,255,255,.35)',
              padding: 32,
              boxSizing: 'border-box'
            }}
          >
            <div style={{ font: "600 18px 'Montserrat Alternates', sans-serif", color: '#fff' }}>
              {t('donate.hero_title')}
            </div>
            <div
              style={{
                font: "400 14px 'Golos Text', sans-serif",
                color: 'rgba(255,255,255,.85)',
                marginTop: 6
              }}
            >
              {t('donate.hero_subtitle')}
            </div>

            {/* qty row */}
            <div
              style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 16 }}
            >
              {PRESETS.map(v => {
                const active = !otherQty && qty === v;
                return (
                  <button
                    key={v}
                    type="button"
                    className={`qty-btn${active ? ' active' : ''}`}
                    onClick={() => {
                      setQty(v);
                      setOtherQty('');
                    }}
                  >
                    {v}
                  </button>
                );
              })}
              <input
                type="number"
                min={1}
                inputMode="numeric"
                placeholder={t('donate.other') as string}
                value={otherQty}
                onChange={e => setOtherQty(e.target.value)}
                className="no-spin"
                style={{
                  flex: 1,
                  minWidth: 150,
                  height: 50,
                  border: 0,
                  borderRadius: 16,
                  padding: '0 16px',
                  font: "400 15px 'Golos Text', sans-serif",
                  color: '#22301A',
                  outline: 'none'
                }}
              />
            </div>

            {/* region picker */}
            <div
              style={{
                font: "600 13px 'Golos Text', sans-serif",
                color: 'rgba(255,255,255,.9)',
                marginTop: 20
              }}
            >
              {t('donate.region_label')}
            </div>
            <div style={{ position: 'relative', marginTop: 9 }}>
              <button
                ref={regionBtnRef}
                type="button"
                onClick={() => setRegionOpen(o => !o)}
                className="f-input"
                style={{
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <span>{regionLabel}</span>
                <span
                  style={{
                    color: '#8FA97C',
                    transform: regionOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 150ms'
                  }}
                >
                  ▾
                </span>
              </button>

              {regionOpen && popRect && createPortal(
                <div
                  ref={regionPopRef}
                  style={{
                    position: 'fixed',
                    left: popRect.left,
                    top: popRect.top,
                    width: popRect.width,
                    zIndex: 100,
                    background: '#14361A',
                    boxShadow: '0 18px 44px rgba(0,0,0,.45)',
                    borderRadius: 16,
                    padding: 10
                  }}
                >
                  <RegionRow
                    name={allLabel}
                    trees={totalRegion}
                    wPct={100}
                    active={regionKey === 'all'}
                    onClick={() => {
                      setRegionKey('all');
                      setRegionOpen(false);
                    }}
                  />
                  {sortedRegions.map(r => (
                    <RegionRow
                      key={r.code}
                      name={getLocalized(r.name, i18n.language)}
                      trees={treesOf(r)}
                      wPct={Math.round((treesOf(r) / maxRegion) * 100)}
                      active={regionKey === r.code}
                      onClick={() => {
                        setRegionKey(r.code);
                        setRegionOpen(false);
                      }}
                    />
                  ))}
                </div>,
                document.body
              )}
            </div>

            <div style={{ height: 1, background: 'rgba(255,255,255,.25)', margin: '18px 0' }} />

            {error && (
              <p style={{ color: '#ffcaca', font: "500 13px 'Golos Text', sans-serif", marginBottom: 10 }}>
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={handleOpenUserDetails}
              disabled={loading || finalCount < 1}
              className="pill-btn"
              style={{
                width: '100%',
                justifyContent: 'center',
                opacity: loading || finalCount < 1 ? 0.6 : 1,
                cursor: loading || finalCount < 1 ? 'not-allowed' : 'pointer'
              }}
            >
              {loading
                ? t('donate.loading')
                : t('donate.submit', { amount: amount.toLocaleString('ru-RU') })}
            </button>
          </div>
        </div>
      </div>

      <a
        href="#plantings-map"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('plantings-map')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
          color: '#fff',
          textDecoration: 'none',
          zIndex: 2,
          cursor: 'pointer',
          paddingBottom: 24,
          animation: 'heroBounce 2s ease-in-out infinite'
        }}
      >
        <span style={{ font: "600 15px 'Golos Text', sans-serif", textShadow: '0 2px 8px rgba(0,0,0,.35)' }}>
          {t('results.tag', 'Наши результаты')}
        </span>
        <span
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: 'rgba(0,0,0,.45)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff'
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </a>

      <UserDetailsModal
        open={userDetailsOpen}
        amount={amount}
        treesCount={finalCount}
        loading={loading}
        showRegionPicker={false}
        onClose={() => setUserDetailsOpen(false)}
        onSubmit={handleUserDetailsSubmit}
      />
      <PaymentMethodModal
        open={modalOpen}
        amount={amount}
        loading={loading}
        onClose={() => {
          setModalOpen(false);
          setPendingPayload(null);
        }}
        onConfirm={handleConfirmGateway}
      />
    </section>
  );
};

const RegionRow: React.FC<{
  name: string;
  trees: number;
  wPct: number;
  active: boolean;
  onClick: () => void;
}> = ({ name, trees, wPct, active, onClick }) => (
  <div
    onClick={onClick}
    style={{
      cursor: 'pointer',
      padding: '7px 8px',
      borderRadius: 10,
      transition: 'background 120ms',
      display: 'flex',
      gap: 10,
      alignItems: 'center'
    }}
    onMouseOver={e => (e.currentTarget.style.background = 'rgba(255,255,255,.08)')}
    onMouseOut={e => (e.currentTarget.style.background = 'transparent')}
  >
    <span
      style={{
        flexShrink: 0,
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: `2px solid ${active ? '#C7F08A' : 'rgba(255,255,255,.5)'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box'
      }}
    >
      {active && (
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#C7F08A' }} />
      )}
    </span>
    <span style={{ flex: 1, minWidth: 0 }}>
      <span
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          font: `${active ? 600 : 500} 12.5px 'Golos Text', sans-serif`,
          color: '#fff',
          marginBottom: 4
        }}
      >
        <span>{name}</span>
        <span style={{ color: 'rgba(255,255,255,.8)' }}>
          {trees.toLocaleString('ru-RU')}
        </span>
      </span>
      <span
        style={{
          display: 'block',
          height: 7,
          borderRadius: 9999,
          background: 'rgba(255,255,255,.18)',
          overflow: 'hidden'
        }}
      >
        <span
          style={{
            display: 'block',
            height: '100%',
            width: `${wPct}%`,
            borderRadius: 9999,
            background: active ? '#C7F08A' : '#5EA82E'
          }}
        />
      </span>
    </span>
  </div>
);

export default HeroSection;
